<template>
  <div class="gps-tracker p-4">
    <div class="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
      <h2 class="mb-0 d-flex align-items-center">
        <MapPin :size="28" class="me-2 text-primary" />
        GPS Tracker
      </h2>
      <div class="d-flex align-items-center gap-2">
        <div class="form-check form-switch mb-0">
          <input
            class="form-check-input"
            type="checkbox"
            id="activeOnlySwitch"
            v-model="activeOnly"
            @change="loadLocations"
          />
          <label class="form-check-label" for="activeOnlySwitch">Clocked in only</label>
        </div>
        <button
          class="btn btn-outline-secondary btn-sm"
          @click="toggleAutoRefresh"
          :title="autoRefresh ? 'Pause auto-refresh' : 'Resume auto-refresh'"
        >
          <i class="fas" :class="autoRefresh ? 'fa-pause' : 'fa-play'"></i>
          {{ autoRefresh ? 'Pause' : 'Live' }}
        </button>
        <button class="btn btn-primary btn-sm" @click="loadLocations" :disabled="loading">
          <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
          Refresh
        </button>
      </div>
    </div>

    <div class="row mb-3">
      <div class="col-12">
        <div class="card">
          <div class="card-body p-0">
            <div ref="mapContainer" class="map-container"></div>
          </div>
        </div>
      </div>
    </div>

    <div class="row" v-if="lastUpdated">
      <div class="col-12">
        <p class="text-muted small mb-0">
          Last updated: {{ lastUpdated }}
          <span v-if="locations.length">
            — showing {{ locations.length }} technician{{ locations.length === 1 ? '' : 's' }}
          </span>
          <span v-else>— no locations found for today</span>
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import { MapPin } from 'lucide-vue-next';
import * as L from 'leaflet';
import 'leaflet.markercluster';
import ApiService from '@/services/ApiService';
import type { TechnicianLocationModel } from '@/models/TimeEntryModel';

const mapContainer = ref<HTMLDivElement | null>(null);
const loading = ref(false);
const locations = ref<TechnicianLocationModel[]>([]);
const activeOnly = ref(false);
const autoRefresh = ref(true);
const lastUpdated = ref<string | null>(null);

let map: L.Map | null = null;
let markerClusterGroup: L.MarkerClusterGroup | null = null;
let refreshTimer: ReturnType<typeof setInterval> | null = null;

const clockedInIcon = L.divIcon({
  className: 'gps-marker gps-marker-active',
  html: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="28" height="28">
    <circle cx="12" cy="12" r="10" fill="#198754" stroke="#fff" stroke-width="2"/>
    <circle cx="12" cy="12" r="4" fill="#fff"/>
  </svg>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14]
});

const clockedOutIcon = L.divIcon({
  className: 'gps-marker gps-marker-inactive',
  html: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="28" height="28">
    <circle cx="12" cy="12" r="10" fill="#6c757d" stroke="#fff" stroke-width="2"/>
    <circle cx="12" cy="12" r="4" fill="#fff"/>
  </svg>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14]
});

function formatTime(dateValue: string | Date): string {
  if (!dateValue) return 'Unknown';
  const date = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;
  return date.toLocaleString();
}

function buildPopup(location: TechnicianLocationModel): string {
  const statusBadge = location.IsClockedIn
    ? '<span class="badge bg-success">Clocked In</span>'
    : '<span class="badge bg-secondary">Clocked Out</span>';

  return `
    <div class="gps-popup">
      <strong>${location.UserName || `User ${location.UserId}`}</strong>
      <div class="mt-1">${statusBadge}</div>
      <div class="text-muted small mt-1">${formatTime(location.Timestamp)}</div>
      <div class="text-muted small">${location.Latitude.toFixed(6)}, ${location.Longitude.toFixed(6)}</div>
    </div>
  `;
}

function createMarkers(locationsToRender: TechnicianLocationModel[]): L.Marker[] {
  return locationsToRender
    .filter(loc => loc.Latitude && loc.Longitude)
    .map(loc => {
      const marker = L.marker([loc.Latitude, loc.Longitude], {
        icon: loc.IsClockedIn ? clockedInIcon : clockedOutIcon,
        title: loc.UserName || `User ${loc.UserId}`
      });

      marker.bindPopup(buildPopup(loc));
      return marker;
    });
}

function fitMapToMarkers() {
  if (!map || !markerClusterGroup || locations.value.length === 0) return;

  const visibleMarkers = markerClusterGroup.getLayers() as L.Marker[];
  if (visibleMarkers.length === 0) return;

  const group = L.featureGroup(visibleMarkers);
  map.fitBounds(group.getBounds().pad(0.1));
}

async function loadLocations() {
  if (loading.value) return;

  loading.value = true;
  try {
    const result = await ApiService.loadLatestLocations(activeOnly.value, undefined, true);
    locations.value = result || [];
    lastUpdated.value = new Date().toLocaleString();

    if (map && markerClusterGroup) {
      markerClusterGroup.clearLayers();
      const markers = createMarkers(locations.value);
      markerClusterGroup.addLayers(markers);
      fitMapToMarkers();
    }
  } catch (err) {
    console.error('Failed to load technician locations', err);
  } finally {
    loading.value = false;
  }
}

function startAutoRefresh() {
  if (refreshTimer) return;
  refreshTimer = setInterval(() => {
    loadLocations();
  }, 60000);
}

function stopAutoRefresh() {
  if (refreshTimer) {
    clearInterval(refreshTimer);
    refreshTimer = null;
  }
}

function toggleAutoRefresh() {
  autoRefresh.value = !autoRefresh.value;
  if (autoRefresh.value) {
    startAutoRefresh();
    loadLocations();
  } else {
    stopAutoRefresh();
  }
}

function initMap() {
  if (!mapContainer.value) return;

  map = L.map(mapContainer.value).setView([39.8283, -98.5795], 4);

  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    maxZoom: 19
  }).addTo(map);

  markerClusterGroup = L.markerClusterGroup({
    spiderfyOnMaxZoom: true,
    showCoverageOnHover: false,
    zoomToBoundsOnClick: true,
    disableClusteringAtZoom: 16
  });

  map.addLayer(markerClusterGroup);
}

onMounted(() => {
  initMap();
  loadLocations();
  startAutoRefresh();
});

onUnmounted(() => {
  stopAutoRefresh();
  if (map) {
    map.remove();
    map = null;
  }
  markerClusterGroup = null;
});
</script>

<style scoped>
.map-container {
  height: 65vh;
  min-height: 400px;
  width: 100%;
}

.gps-tracker :deep(.gps-marker) {
  background: transparent;
  border: none;
}

.gps-tracker :deep(.gps-popup) {
  min-width: 160px;
}
</style>
